/**
 * aiDataSync — keeps the UI fresh after the assistant writes data.
 *
 * The server marks each write-tier tool result with the `domains` (arche
 * ids) it touched. This module translates those into:
 *  1. React Query invalidations on the mapped `queryKeys` roots (never raw
 *     string arrays — repo norm), and
 *  2. a `fias-ai:data-changed` CustomEvent + the active arche's optional
 *     `onExternalDataChange` callback, for pages whose server state is NOT
 *     in React Query (the calendar's useEvents is useState + manual
 *     refetch).
 *
 * Fired per successful write tool result — the user may be looking at the
 * affected page while the agent keeps working.
 */

import type { QueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import type { ArcheAICapabilities } from '../types';

export const AI_DATA_CHANGED_EVENT = 'fias-ai:data-changed';

/** Server write domains (arche ids) → React Query roots to invalidate. */
const DOMAIN_QUERY_ROOTS: Record<string, ReadonlyArray<readonly unknown[]>> = {
  arche_contact_book: [queryKeys.contactBook.all],
  arche_calendar: [queryKeys.calendarSync.all],
  arche_fias_leads: [queryKeys.fiasLeads.all],
  arche_email: [queryKeys.email.all],
  arche_fias_transfer: [queryKeys.fiasTransfer.all],
};

export function applyAiWrites(
  queryClient: QueryClient,
  domains: string[],
  activeCapabilities:
    | (ArcheAICapabilities & {
        onExternalDataChange?: (domains: string[]) => void;
      })
    | null,
): void {
  for (const domain of domains) {
    for (const root of DOMAIN_QUERY_ROOTS[domain] ?? []) {
      void queryClient.invalidateQueries({ queryKey: root as unknown[] });
    }
  }
  window.dispatchEvent(new CustomEvent(AI_DATA_CHANGED_EVENT, { detail: { domains } }));
  activeCapabilities?.onExternalDataChange?.(domains);
}
